// Stepped floors (CIN-15118): turns the row rises of a stadium room into what a builder would pour --
// continuous tiers that run wall to wall (aisles included), aisle stairs between them with the German
// step proportions, nosing lines for step lights, and handrails. One implementation for the studio's
// 3D view, its 2D plan and the guest seat map, so the three cannot disagree about where a step is.
// Plan space (mm, y toward the screen, x to the right); heights are mm above the room floor.
// Pure: relative imports only, so node --test loads it.

import { median } from './math.ts';
import { pointInOutline, scanlineIntervals, tessellate } from './outline/outline.ts';
import { clipPolylineInside, intersectPolygons } from './outline/polygon-clip.ts';
import { levelOf } from './outline/room-geometry.ts';
import type { RoomGeometry, RoomLevel, RoomOutline } from './outline/types.ts';
import type { TierPlatformSpec } from './room-design.ts';

export type PlanPoint = { x: number; y: number };
export type PlanPoint3 = { x: number; y: number; z: number };

/**
 * What the model needs of a seat (a `StudioSeat` fits). `rowName` names the platform the seat stands on for the
 * design's platform entries; without it no entry applies.
 */
export type TierSeat = {
    x: number;
    y: number;
    z: number;
    width: number;
    depth: number;
    rotation: number;
    rowName?: string;
};

/** Where a platform ends and how deep its walkway is, mm, after the limits (CIN-15200); the inspector shows these. */
export type TierExtent = {
    /** How far the platform stops short of the left wall (0 = at the wall) and how far it may: up to its outermost seat. */
    left: number;
    maxLeft: number;
    right: number;
    maxRight: number;
    /** Floor between the front row's seats and the riser, and the most there is room for (up to the row in front). */
    front: number;
    maxFront: number;
};

/** A side face where a shortened platform ends: the drop from its top down to the level beside it. */
export type TierSide = { line: PlanPoint[]; baseZ: number };

export type TierPlatform = {
    /** Top face, counter-clockwise seen from above, wall to wall (or to where the design ends it). */
    polygon: PlanPoint[];
    /** The riser edge in front, left to right, wall to wall (or to where the design ends it). */
    front: PlanPoint[];
    /** Height of the top face. */
    z: number;
    /** Height of the level in front of the riser (the floor, or the tier below). */
    baseZ: number;
    /** The room level (balcony) this tier belongs to; absent = the main floor. */
    level?: string;
    /**
     * Outline rooms only (CIN-15126): the top face clipped to the room outline, as counter-clockwise rings (an L
     * can split a band in two). `polygon` stays the unclipped band, which `tierHeightAt` reads.
     */
    pieces?: PlanPoint[][];
    /** Outline rooms only: the parts of the riser edge that lie inside the room, each a left-to-right polyline. */
    risers?: PlanPoint[][];
    /** The name of the platform's front row, which the design's platform entries use; absent for unnamed seats. */
    row?: string;
    extent: TierExtent;
    /** The side faces where the design ends the platform before a wall; absent when it runs wall to wall. */
    sides?: TierSide[];
};

export type TierStep = {
    polygon: PlanPoint[];
    /** Height of the tread. */
    z: number;
    /** Height of this step's riser. */
    riser: number;
    /** Height of the level the step stands on. */
    baseZ: number;
    /** The front edge of the tread, left to right: where the step light goes. */
    nosing: [PlanPoint, PlanPoint];
    /** `false` for the tread that is the tier's own top: it only carries a nosing light. */
    isSolid: boolean;
    /** The room level this step belongs to; absent = the main floor. */
    level?: string;
};

export type TierLine = { a: PlanPoint; b: PlanPoint; z: number };

/** A guard wall derived on the upper level's edge where a tier rises steeply (split level), with gaps for the stairs. */
export type TierBarrier = {
    /** The riser edge (left to right), already clipped to the room outline. */
    line: PlanPoint[];
    /** Height of the level the wall stands on (the tier top). */
    z: number;
    /** Wall height above `z`. */
    height: number;
    /** x ranges (plan mm) where a stair passes through the wall. */
    openings: [number, number][];
};

/** The outer rail of a side staircase; the solid wall stands under it. `inward` is the side of the stairs: +1 = toward +x. */
export type TierStairWall = { line: PlanPoint3[]; inward: 1 | -1 };

/** A railing along the open side of a cross aisle. */
export type TierRailing = { line: PlanPoint[]; z: number; height: number };

export type TierModel = {
    tiers: TierPlatform[];
    steps: TierStep[];
    /** Handrails as 3D polylines (plan mm, z = mm above the floor), front to back. */
    handrails: PlanPoint3[][];
    /** Riser edges of the tiers, split around the aisle stairs, for step lights. */
    edgeLights: TierLine[];
    /** Guide lines along both sides of every aisle, for aisle lights. */
    aisleLights: TierLine[];
    /** Guard walls at steep risers (split levels). Shown when the design asks for derived barriers. */
    barriers: TierBarrier[];
    /** Railings along cross aisles. */
    railings: TierRailing[];
    /**
     * Rail lines (the same points as `handrails`) of stairs that run along a wall: the outer rail of a side
     * staircase, where the design's stair side walls go.
     */
    stairWalls: TierStairWall[];
    /**
     * The slabs of the room's extra levels (balconies), for the height queries: each level's floor height and
     * footprint. Absent for a room without levels.
     */
    levels?: TierLevel[];
};

/** A balcony as the height queries see it: its floor (the slab top) over a footprint. */
export type TierLevel = { id: string; elevation: number; ring: PlanPoint[] };

const EMPTY_TIER_MODEL: TierModel = {
    tiers: [],
    steps: [],
    handrails: [],
    edgeLights: [],
    aisleLights: [],
    barriers: [],
    railings: [],
    stairWalls: []
};

/** Free floor kept in front of a row's seats before the riser (the walkway along the row). */
const TIER_WALKWAY = 400;
/** An opening between seat blocks at least this wide (mm) is an aisle. */
const MIN_AISLE = 900;
/** Rises up to this (mm) are a single step; above it the rise is split. */
const SINGLE_STEP_LIMIT = 200;
export const MAX_RISER = 190;
export const MIN_TREAD = 260;
/** Step rule 2h + a = 630 mm. */
const STEP_RULE = 630;
export const HANDRAIL_HEIGHT = 900;
/** A tier that rises at least this much (mm) over the level in front is a split level and gets a guard wall. */
export const BARRIER_MIN_RISE = 500;
export const BARRIER_HEIGHT = 900;
/** Free width (mm) of a walkway between two blocks that makes it a cross aisle. */
export const CROSS_AISLE_MIN = 1200;
const RAILING_HEIGHT = 1000;
/** A cross-aisle railing stands this far (mm) behind the seat backs of the block in front. */
const CROSS_AISLE_RAILING_OFFSET = 150;
/** A stair whose outer rail is this close (mm) to a wall counts as a side staircase. */
const SIDE_STAIR_WALL_DISTANCE = 700;
/** A shortened platform still reaches this far (mm) past the outermost seat on it: no seat stands on its edge. */
export const PLATFORM_SEAT_MARGIN = 200;
/** How far (mm) the riser of the front platform may stand ahead of its row when no row in front limits it. */
const MAX_FRONT_WALKWAY = 3000;

const FLAT_SPREAD = 30;
const FLOOR_LEVEL = 10;
const SAME_LEVEL = 15;
const ROW_TOLERANCE = 250;
const EDGE_MARGIN = 300;
const CURVE_TOLERANCE = 15;
const MAX_EXTENSION_SLOPE = 0.5;
const AISLE_CLUSTER = 500;
const AISLE_RECURRENCE = 0.4;
const MAX_WALL_AISLE = 2200;
const STAIR_WIDTH_CAP = 1200;
const STAIR_WIDTH_MIN = 500;
const STAIR_CLEARANCE = 100;
const MAX_STEPS_PER_RISE = 6;
const MAX_PLAUSIBLE_RISE_MM = 4000;
const GARBAGE_RISE_TOTAL_MM = 1000;

const rad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Row rise in mm for a seat `z`. Implausible ranges (ticket systems store row numbers as heights) are
 * squeezed into one meter of tiers, so an imported room never grows a tower.
 */
export function resolveRise(seats: readonly { z: number }[]): (z: number) => number {
    if (seats.length === 0) return () => 0;
    let min = Infinity;
    let max = -Infinity;
    for (const s of seats) {
        min = Math.min(min, s.z);
        max = Math.max(max, s.z);
    }
    const range = max - min;
    if (range > MAX_PLAUSIBLE_RISE_MM) {
        const scale = GARBAGE_RISE_TOTAL_MM / range;
        return (z) => (z - min) * scale;
    }
    return (z) => Math.max(0, z);
}

/** Slope of a seat's row (dy per dx) taken from its rotation: a seat facing r has its row along r + 90. */
function rowSlope(seat: { rotation: number }): number {
    const cos = Math.cos(rad(seat.rotation));
    return Math.abs(cos) < 0.2 ? 0 : -Math.sin(rad(seat.rotation)) / cos;
}

/**
 * Groups seats into rows, front row (largest y) first, each sorted left to right. Curved rows are
 * followed along the seat rotation, so a row whose ends sit 600 mm nearer the screen than its middle
 * stays one row. The studio's row numbering uses this too. `tolerance` (mm) is how far a seat may sit from
 * the line of the row it joins.
 */
export function clusterTierRows<T extends { x: number; y: number; rotation: number }>(
    seats: readonly T[],
    tolerance = ROW_TOLERANCE
): T[][] {
    const sorted = [...seats].sort((a, b) => a.x - b.x || b.y - a.y);
    const rows: T[][] = [];
    for (const seat of sorted) {
        let best: T[] | null = null;
        let bestDelta = Infinity;
        for (const row of rows) {
            const tail = row[row.length - 1];
            const delta = Math.abs(seat.y - (tail.y + rowSlope(tail) * (seat.x - tail.x)));
            if (delta <= tolerance && delta < bestDelta) {
                best = row;
                bestDelta = delta;
            }
        }
        if (best) best.push(seat);
        else rows.push([seat]);
    }
    const middle = sorted.length ? (sorted[0].x + sorted[sorted.length - 1].x) / 2 : 0;
    const yAtMiddle = (row: T[]) => {
        const nearest = row.reduce((a, b) => (Math.abs(b.x - middle) < Math.abs(a.x - middle) ? b : a));
        return nearest.y + rowSlope(nearest) * (middle - nearest.x);
    };
    return rows
        .map((row) => ({ row, y: yAtMiddle(row) }))
        .sort((a, b) => b.y - a.y)
        .map(({ row }) => row);
}

// --- Polylines ------------------------------------------------------------------------------------

/** Douglas-Peucker on a polyline that is a function of x. */
function simplify(points: PlanPoint[], tolerance: number): PlanPoint[] {
    if (points.length <= 2) return points;
    const keep = new Array<boolean>(points.length).fill(false);
    keep[0] = true;
    keep[points.length - 1] = true;
    const stack: [number, number][] = [[0, points.length - 1]];
    while (stack.length > 0) {
        const [from, to] = stack.pop() as [number, number];
        const a = points[from];
        const b = points[to];
        let worst = -1;
        let worstDistance = tolerance;
        for (let i = from + 1; i < to; i++) {
            const t = b.x === a.x ? 0 : (points[i].x - a.x) / (b.x - a.x);
            const distance = Math.abs(points[i].y - (a.y + (b.y - a.y) * t));
            if (distance > worstDistance) {
                worst = i;
                worstDistance = distance;
            }
        }
        if (worst >= 0) {
            keep[worst] = true;
            stack.push([from, worst], [worst, to]);
        }
    }
    return points.filter((_, i) => keep[i]);
}

/** y of a left-to-right polyline at x; the end segments are extended. */
function polylineYAt(points: readonly PlanPoint[], x: number): number {
    if (points.length === 1) return points[0].y;
    let i = 1;
    while (i < points.length - 1 && points[i].x < x) i++;
    const a = points[i - 1];
    const b = points[i];
    if (b.x === a.x) return b.y;
    return a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x);
}

/** A line along a row, `reachOf(seat)` mm ahead of each seat (behind it when negative), following the arc, extended to both walls. */
function edgeLine(row: TierSeat[], reachOf: (seat: TierSeat) => number, xMin: number, xMax: number): PlanPoint[] {
    const raw: PlanPoint[] = row
        .map((s) => {
            const reach = reachOf(s);
            return { x: s.x + Math.sin(rad(s.rotation)) * reach, y: s.y + Math.cos(rad(s.rotation)) * reach };
        })
        .sort((a, b) => a.x - b.x);
    const points: PlanPoint[] = [];
    for (const p of raw) {
        const last = points[points.length - 1];
        if (last && Math.abs(last.x - p.x) < 1) points[points.length - 1] = { x: last.x, y: (last.y + p.y) / 2 };
        else points.push(p);
    }
    const line = simplify(points, CURVE_TOLERANCE);
    const slopeAt = (a: PlanPoint, b: PlanPoint) =>
        b.x === a.x ? 0 : Math.max(-MAX_EXTENSION_SLOPE, Math.min(MAX_EXTENSION_SLOPE, (b.y - a.y) / (b.x - a.x)));
    const first = line[0];
    const last = line[line.length - 1];
    const startSlope = line.length > 1 ? slopeAt(line[0], line[1]) : 0;
    const endSlope = line.length > 1 ? slopeAt(line[line.length - 2], last) : 0;
    const out = [...line];
    if (first.x > xMin) out.unshift({ x: xMin, y: first.y + startSlope * (xMin - first.x) });
    if (last.x < xMax) out.push({ x: xMax, y: last.y + endSlope * (xMax - last.x) });
    return out;
}

/** The riser line of a row: 0.4 m ahead of its seats, following the arc, extended to both walls. */
function frontLine(row: TierSeat[], walkway: number, xMin: number, xMax: number): PlanPoint[] {
    return edgeLine(row, (s) => s.depth / 2 + walkway, xMin, xMax);
}

// --- Aisles ---------------------------------------------------------------------------------------

type AisleCandidate = { x: number; width: number };
type Aisle = { row: number; candidate: AisleCandidate }[];

/** The walls' x range at a row: the whole room for a rectangle, the outline's span at the row's depth otherwise. */
type WallSpan = (row: TierSeat[]) => [number, number];

function rowAisles(row: TierSeat[], span: WallSpan): AisleCandidate[] {
    const [xMin, xMax] = span(row);
    const found: AisleCandidate[] = [];
    for (let i = 1; i < row.length; i++) {
        const a = row[i - 1];
        const b = row[i];
        const direction = rad((a.rotation + b.rotation) / 2);
        const along = (b.x - a.x) * Math.cos(direction) - (b.y - a.y) * Math.sin(direction);
        const gap = along - (a.width + b.width) / 2;
        if (gap >= MIN_AISLE) found.push({ x: (a.x + b.x) / 2, width: gap });
    }
    // Between the wall and the outermost seat: an aisle only when it is a passage, not just empty floor.
    const first = row[0];
    const last = row[row.length - 1];
    const leftMargin = first.x - first.width / 2 - xMin;
    if (leftMargin >= MIN_AISLE && leftMargin <= MAX_WALL_AISLE) {
        const width = Math.min(leftMargin, STAIR_WIDTH_CAP);
        found.push({ x: first.x - first.width / 2 - width / 2, width });
    }
    const rightMargin = xMax - (last.x + last.width / 2);
    if (rightMargin >= MIN_AISLE && rightMargin <= MAX_WALL_AISLE) {
        const width = Math.min(rightMargin, STAIR_WIDTH_CAP);
        found.push({ x: last.x + last.width / 2 + width / 2, width });
    }
    return found;
}

/** Aisle candidates of every row, joined into aisles that recur down the block. */
function findAisles(rows: TierSeat[][], span: WallSpan): Aisle[] {
    if (rows.length < 2) return [];
    const all = rows.flatMap((row, index) => rowAisles(row, span).map((candidate) => ({ row: index, candidate })));
    all.sort((a, b) => a.candidate.x - b.candidate.x);
    const clusters: Aisle[] = [];
    for (const item of all) {
        const cluster = clusters.find((c) => Math.abs(c[c.length - 1].candidate.x - item.candidate.x) <= AISLE_CLUSTER);
        if (cluster) cluster.push(item);
        else clusters.push([item]);
    }
    const needed = Math.max(2, Math.ceil(rows.length * AISLE_RECURRENCE));
    return clusters.filter((cluster) => new Set(cluster.map((c) => c.row)).size >= needed);
}

// --- Model ----------------------------------------------------------------------------------------

type Group = { rows: TierSeat[][]; z: number; walkway: number; gap: number };

function stepPlan(rise: number): { count: number; riser: number; tread: number } {
    const count = rise <= SINGLE_STEP_LIMIT ? 1 : Math.min(MAX_STEPS_PER_RISE, Math.ceil(rise / MAX_RISER));
    const riser = rise / count;
    return { count, riser, tread: Math.max(MIN_TREAD, Math.round(STEP_RULE - 2 * riser)) };
}

/** Removes the parts of a segment inside any of the x intervals. */
function clipAround(a: PlanPoint, b: PlanPoint, holes: [number, number][]): [PlanPoint, PlanPoint][] {
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    let pieces: [number, number][] = [[lo, hi]];
    for (const [h0, h1] of holes) {
        pieces = pieces.flatMap(([p0, p1]): [number, number][] => {
            if (h1 <= p0 || h0 >= p1) return [[p0, p1]];
            const out: [number, number][] = [];
            if (h0 > p0) out.push([p0, h0]);
            if (h1 < p1) out.push([h1, p1]);
            return out;
        });
    }
    const left = a.x <= b.x ? a : b;
    const right = a.x <= b.x ? b : a;
    const at = (x: number): PlanPoint => ({
        x,
        y: right.x === left.x ? left.y : left.y + ((right.y - left.y) * (x - left.x)) / (right.x - left.x)
    });
    return pieces.filter(([p0, p1]) => p1 - p0 > 20).map(([p0, p1]) => [at(p0), at(p1)]);
}

/** The outline's x span at the depth of a row, around the row's middle; the whole box when the outline has none there. */
function outlineSpanAt(outline: RoomOutline, row: readonly TierSeat[], fallback: [number, number]): [number, number] {
    const y = row.reduce((sum, s) => sum + s.y, 0) / row.length;
    const x = row.reduce((sum, s) => sum + s.x, 0) / row.length;
    const spans = scanlineIntervals(outline, y);
    if (spans.length === 0) return fallback;
    const containing = spans.find(([lo, hi]) => x >= lo && x <= hi);
    if (containing) return containing;
    return spans.reduce((best, span) => {
        const distance = (s: [number, number]) => Math.min(Math.abs(x - s[0]), Math.abs(x - s[1]));
        return distance(span) < distance(best) ? span : best;
    });
}

/** A room as the tiers see it: the box in mm, plus the optional outline geometry (CIN-15126). */
export type TierRoom = { width: number; depth: number; geometry?: RoomGeometry | null };

/**
 * The stepped floor of a room. `room` is the drawn room in mm (the platforms run from wall to wall
 * and are widened to whatever seats poke out of it). Flat rooms return an empty model.
 *
 * With an outline (`room.geometry.outline`) the platforms are clipped to it (`pieces`, `risers`), stairs that
 * would stand outside are dropped, and wall aisles are found against the wall at each row's depth. Split
 * levels and cross aisles get derived `barriers` and `railings`; side staircases get `stairWalls`.
 *
 * `platforms` (the design's `tiers.platforms`, CIN-15200) end a platform before a wall or move its riser. Where a
 * platform is absent the level in front of it carries on back (or the floor shows), its end gets a side face
 * (`sides`), and the stairs, lights, guard walls and railings on it stop with it. Without entries the model is the
 * same as without the argument.
 */
export function deriveTiers(
    seats: readonly TierSeat[],
    room: TierRoom,
    platforms: readonly TierPlatformSpec[] = []
): TierModel {
    const geometry = room.geometry;
    if (!geometry?.levels?.length)
        return deriveFloor(
            seats,
            room,
            platforms.filter((p) => !p.level)
        );
    return deriveLevels(seats, room, geometry, platforms);
}

/** Half the x span of a seat's footprint, turned. */
function halfSpanX(seat: TierSeat): number {
    return (
        Math.abs((seat.width / 2) * Math.cos(rad(seat.rotation))) +
        Math.abs((seat.depth / 2) * Math.sin(rad(seat.rotation)))
    );
}

/** The part of a left-to-right polyline between `a` and `b` (a < b), ends interpolated (or extended). */
function clipLine(line: readonly PlanPoint[], a: number, b: number): PlanPoint[] {
    return [
        { x: a, y: polylineYAt(line, a) },
        ...line.filter((p) => p.x > a && p.x < b),
        { x: b, y: polylineYAt(line, b) }
    ];
}

/** The tiers of a floor whose seats stand at `z` mm above THAT floor. */
function deriveFloor(
    seats: readonly TierSeat[],
    room: TierRoom,
    platforms: readonly TierPlatformSpec[] = []
): TierModel {
    if (seats.length === 0) return EMPTY_TIER_MODEL;
    const riseOf = resolveRise(seats);
    const risen = seats.map((s) => ({ ...s, z: riseOf(s.z) }));
    if (Math.max(...risen.map((s) => s.z)) < FLAT_SPREAD) return EMPTY_TIER_MODEL;

    const xMin = Math.min(-room.width / 2, ...risen.map((s) => s.x - s.width / 2 - EDGE_MARGIN));
    const xMax = Math.max(room.width / 2, ...risen.map((s) => s.x + s.width / 2 + EDGE_MARGIN));
    const yBack = Math.min(-room.depth, ...risen.map((s) => s.y - s.depth / 2 - EDGE_MARGIN));
    const rows = clusterTierRows(risen);
    const outline: RoomOutline | null = room.geometry?.outline ?? null;
    const ring = outline ? tessellate(outline).points : null;
    const span: WallSpan = outline ? (row) => outlineSpanAt(outline, row, [xMin, xMax]) : () => [xMin, xMax];

    // Rows on the same level share a tier.
    const groups: Group[] = [];
    let previousRow: TierSeat[] | null = null;
    for (const row of rows) {
        const z = Math.round(median(row.map((s) => s.z)));
        let walkway = TIER_WALKWAY;
        let gap = 0;
        if (previousRow) {
            const back = median(previousRow.map((s) => s.y - s.depth / 2));
            const front = median(row.map((s) => s.y + s.depth / 2));
            gap = back - front;
            walkway = Math.max(0, Math.min(TIER_WALKWAY, gap));
        }
        const last = groups[groups.length - 1];
        if (last && Math.abs(last.z - z) <= SAME_LEVEL) last.rows.push(row);
        else groups.push({ rows: [row], z, walkway, gap });
        previousRow = row;
    }

    // The design's platform entries (CIN-15200): a raised group takes the entry of a row standing on it. Its riser
    // moves up to the seats of the row in front, never under them.
    const specs = groups.map((group) => {
        if (group.z <= FLOOR_LEVEL || platforms.length === 0) return undefined;
        const names = new Set(group.rows.flatMap((row) => row.map((s) => s.rowName)));
        return platforms.find((p) => names.has(p.row));
    });
    const maxFronts = groups.map((group, k) => (k > 0 ? Math.max(0, Math.floor(group.gap)) : MAX_FRONT_WALKWAY));
    groups.forEach((group, k) => {
        const front = specs[k]?.front;
        if (front !== undefined) group.walkway = Math.min(front, maxFronts[k]);
    });

    const fronts = groups.map((g) => frontLine(g.rows[0], g.walkway, xMin, xMax));
    const backWall: PlanPoint[] = [
        { x: xMin, y: yBack },
        { x: xMax, y: yBack }
    ];

    // Where each platform ends: wall to wall unless its entry ends it earlier, never closer to a seat on it than the margin.
    const extents = groups.map((group, k) => {
        const [wallLo, wallHi] = span(group.rows[0]);
        const all = group.rows.flat();
        const seatLo = Math.min(...all.map((s) => s.x - halfSpanX(s))) - PLATFORM_SEAT_MARGIN;
        const seatHi = Math.max(...all.map((s) => s.x + halfSpanX(s))) + PLATFORM_SEAT_MARGIN;
        const maxLeft = Math.max(0, Math.floor(seatLo - wallLo));
        const maxRight = Math.max(0, Math.floor(wallHi - seatHi));
        const left = Math.min(specs[k]?.left ?? 0, maxLeft);
        const right = Math.min(specs[k]?.right ?? 0, maxRight);
        const extent: TierExtent = { left, maxLeft, right, maxRight, front: group.walkway, maxFront: maxFronts[k] };
        return { lo: left > 0 ? wallLo + left : xMin, hi: right > 0 ? wallHi - right : xMax, extent };
    });
    const shortened = extents.some((e) => e.lo !== xMin || e.hi !== xMax);
    const covers = (k: number, x: number) => extents[k].lo <= x && x <= extents[k].hi;
    /** Height of the level in front of group k at x: the nearest group ahead whose platform is there, else the floor. */
    const levelBefore = (k: number, x: number): number => {
        for (let i = k - 1; i >= 0; i--) if (covers(i, x)) return groups[i].z;
        return 0;
    };
    /** lo, hi and every end of the `others` platforms between them, sorted. */
    const cutsWithin = (lo: number, hi: number, others: typeof extents): number[] =>
        [...new Set([lo, hi, ...others.flatMap((e) => [e.lo, e.hi]).filter((x) => x > lo && x < hi)])].sort(
            (a, b) => a - b
        );
    const namedRow = (group: Group) => (group.rows[0][0].rowName ? { row: group.rows[0][0].rowName } : {});
    // A platform among shortened ones: its back follows, x by x, the first platform behind it that is there (else the
    // back wall), so the level in front carries on where a platform is cut away. Its top is its outline (`pieces`),
    // its riser drops to the lowest level in front, and every end before a wall is a side face.
    const cutPlatform = (k: number, riser: PlanPoint[]): TierPlatform => {
        const { lo, hi, extent } = extents[k];
        const back: PlanPoint[] = [];
        const cuts = cutsWithin(lo, hi, extents.slice(k + 1));
        for (let i = 1; i < cuts.length; i++) {
            const mid = (cuts[i - 1] + cuts[i]) / 2;
            const next = groups.findIndex((_, m) => m > k && covers(m, mid));
            for (const p of clipLine(next >= 0 ? fronts[next] : backWall, cuts[i - 1], cuts[i])) {
                const last = back[back.length - 1];
                if (!last || last.x !== p.x || last.y !== p.y) back.push(p);
            }
        }
        const below = cutsWithin(lo, hi, extents.slice(0, k));
        let baseZ = Infinity;
        for (let i = 1; i < below.length; i++) baseZ = Math.min(baseZ, levelBefore(k, (below[i - 1] + below[i]) / 2));
        const sides: TierSide[] = [];
        if (lo !== xMin) sides.push({ line: [riser[0], back[0]], baseZ: levelBefore(k, lo - 1) });
        if (hi !== xMax) {
            sides.push({ line: [riser[riser.length - 1], back[back.length - 1]], baseZ: levelBefore(k, hi + 1) });
        }
        const polygon = [...riser, ...[...back].reverse()];
        return {
            polygon,
            front: riser,
            z: groups[k].z,
            baseZ,
            pieces: [polygon],
            risers: [riser],
            ...namedRow(groups[k]),
            extent,
            ...(sides.length > 0 ? { sides } : {})
        };
    };
    const aisles = findAisles(rows, span);
    const rowIndex = new Map(rows.map((row, i) => [row, i]));

    const tiers: TierPlatform[] = [];
    const steps: TierStep[] = [];
    const edgeLights: TierLine[] = [];
    const aisleLights: TierLine[] = [];
    // Per aisle: the rail points of the current run of stairs, cut when a tier has none.
    type RailRun = { left: PlanPoint3[]; right: PlanPoint3[]; wallLeft: boolean; wallRight: boolean };
    const railRuns: RailRun[][] = aisles.map(() => []);
    const openRun: (RailRun | null)[] = aisles.map(() => null);
    const closeRun = (ai: number) => {
        const open = openRun[ai];
        if (open) {
            railRuns[ai].push(open);
            openRun[ai] = null;
        }
    };
    const barrierSources: { front: PlanPoint[]; z: number; openings: [number, number][] }[] = [];
    const railingSources: { line: PlanPoint[]; z: number }[] = [];

    // Where each aisle sits at each group: the candidate of the first row of the group that has it.
    const rawAisleAt = (aisle: Aisle, group: Group): AisleCandidate | null => {
        for (const row of group.rows) {
            const hit = aisle.find((c) => c.row === rowIndex.get(row));
            if (hit) return hit.candidate;
        }
        return null;
    };
    // A missing seat widens the gap in its row and shifts the middle of the aisle there; the stairs would jog
    // sideways for that one tier and the handrails with them. A tier between two others that have the aisle takes
    // the median of the three, which leaves a straight or a fanning aisle untouched.
    const placements = aisles.map((aisle) => {
        const raw = groups.map((group) => rawAisleAt(aisle, group));
        return raw.map((candidate, k): AisleCandidate | null => {
            const before = raw[k - 1];
            const after = raw[k + 1];
            if (!candidate || !before || !after) return candidate;
            return {
                x: median([before.x, candidate.x, after.x]),
                width: median([before.width, candidate.width, after.width])
            };
        });
    });

    let baseZ = 0;
    const stairPlans: ({ count: number; riser: number; tread: number } | null)[] = [];
    groups.forEach((group) => {
        const rise = group.z - baseZ;
        stairPlans.push(rise >= SAME_LEVEL && group.z > FLOOR_LEVEL ? stepPlan(rise) : null);
        baseZ = group.z;
    });

    baseZ = 0;
    groups.forEach((group, k) => {
        const front = fronts[k];
        const back = k + 1 < groups.length ? fronts[k + 1] : backWall;
        const plan = stairPlans[k];
        const isTier = group.z > FLOOR_LEVEL;
        // The riser edge as far as the platform reaches; everything along it (lights, guard wall) stops there too.
        const riser = shortened ? clipLine(front, extents[k].lo, extents[k].hi) : front;
        if (isTier) {
            tiers.push(
                shortened
                    ? cutPlatform(k, riser)
                    : {
                          polygon: [...front, ...[...back].reverse()],
                          front,
                          z: group.z,
                          baseZ,
                          ...namedRow(group),
                          extent: extents[k].extent
                      }
            );
        }

        // Stairs in every aisle that reaches this tier.
        const holes: [number, number][] = [];
        aisles.forEach((_, ai) => {
            const candidate = placements[ai][k];
            if (!candidate) {
                closeRun(ai);
                return;
            }
            const width = Math.max(STAIR_WIDTH_MIN, Math.min(STAIR_WIDTH_CAP, candidate.width - STAIR_CLEARANCE));
            let xa = candidate.x - width / 2;
            let xb = candidate.x + width / 2;
            // A platform ended before the wall keeps the part of a stair that still stands on it, if that is a stair.
            if (shortened && isTier) {
                xa = Math.max(xa, extents[k].lo);
                xb = Math.min(xb, extents[k].hi);
                if (xb - xa < STAIR_WIDTH_MIN) {
                    closeRun(ai);
                    return;
                }
            }
            const ya = polylineYAt(front, xa);
            const yb = polylineYAt(front, xb);
            // An aisle whose stair would stand outside the room (a notch, beyond a slanted wall) has none.
            if (outline && !pointInOutline(outline, { x: candidate.x, y: (ya + yb) / 2 }, 1)) {
                closeRun(ai);
                return;
            }

            // A guide light along each side, from this tier's edge to where the next one's stairs begin.
            const nextPlan = stairPlans[k + 1];
            const nextFront = k + 1 < groups.length ? fronts[k + 1] : null;
            for (const [x, y0] of [
                [xa, ya],
                [xb, yb]
            ] as const) {
                const y1 = nextFront
                    ? polylineYAt(nextFront, x) + (nextPlan ? (nextPlan.count - 1) * nextPlan.tread : 0)
                    : Math.min(...group.rows.flat().map((s) => s.y)) - 400;
                if (y0 - y1 > 100) aisleLights.push({ a: { x, y: y0 }, b: { x, y: y1 }, z: group.z });
            }

            // Where the platform in front is cut away, the stair climbs from the level that is there instead.
            const stairBase = shortened ? levelBefore(k, (xa + xb) / 2) : baseZ;
            const stairPlan =
                stairBase === baseZ ? plan : group.z - stairBase >= SAME_LEVEL ? stepPlan(group.z - stairBase) : null;
            if (!stairPlan || !isTier) {
                closeRun(ai);
                return;
            }
            holes.push([xa, xb]);
            const [wallLo, wallHi] = span(group.rows[0]);
            const run = openRun[ai] ?? {
                left: [],
                right: [],
                wallLeft: xa - wallLo <= SIDE_STAIR_WALL_DISTANCE,
                wallRight: wallHi - xb <= SIDE_STAIR_WALL_DISTANCE
            };
            openRun[ai] = run;
            for (let m = stairPlan.count - 1; m >= 0; m--) {
                const top = group.z - m * stairPlan.riser;
                const na: PlanPoint = { x: xa, y: ya + m * stairPlan.tread };
                const nb: PlanPoint = { x: xb, y: yb + m * stairPlan.tread };
                steps.push({
                    polygon: [
                        { x: xa, y: ya + (m - 1) * stairPlan.tread },
                        { x: xb, y: yb + (m - 1) * stairPlan.tread },
                        nb,
                        na
                    ],
                    z: top,
                    riser: stairPlan.riser,
                    baseZ: stairBase,
                    nosing: [na, nb],
                    isSolid: m > 0
                });
                run.left.push({ x: xa, y: na.y, z: top + HANDRAIL_HEIGHT });
                run.right.push({ x: xb, y: nb.y, z: top + HANDRAIL_HEIGHT });
            }
        });

        if (isTier) {
            for (let i = 1; i < riser.length; i++) {
                for (const [a, b] of clipAround(riser[i - 1], riser[i], holes)) {
                    edgeLights.push({ a, b, z: group.z });
                }
            }
        }
        const rise = group.z - baseZ;
        if (isTier && rise >= BARRIER_MIN_RISE) {
            barrierSources.push({ front: riser, z: group.z, openings: holes });
            // A cross aisle: the walkway between the front block and this raised block is wide enough to be one.
            if (k > 0 && group.gap - TIER_WALKWAY >= CROSS_AISLE_MIN) {
                const lastRow = groups[k - 1].rows[groups[k - 1].rows.length - 1];
                const line = edgeLine(lastRow, (s) => -(s.depth / 2 + CROSS_AISLE_RAILING_OFFSET), xMin, xMax);
                // The railing stands on the platform in front and ends with it.
                railingSources.push({
                    line: shortened ? clipLine(line, extents[k - 1].lo, extents[k - 1].hi) : line,
                    z: baseZ
                });
            }
        }
        baseZ = group.z;
    });
    openRun.forEach((_, ai) => closeRun(ai));

    const handrails = railRuns
        .flat()
        .flatMap((run) => [run.left, run.right])
        .filter((line) => line.length >= 2);
    const stairWalls: TierStairWall[] = railRuns
        .flat()
        .flatMap((run): TierStairWall[] => [
            ...(run.wallLeft ? [{ line: run.left, inward: 1 as const }] : []),
            ...(run.wallRight ? [{ line: run.right, inward: -1 as const }] : [])
        ])
        .filter((wall) => wall.line.length >= 2);

    const clipped = (line: PlanPoint[]): PlanPoint[][] => (ring ? clipPolylineInside(line, ring) : [line]);
    const barriers: TierBarrier[] = barrierSources.flatMap((source) =>
        clipped(source.front).map((line) => ({
            line,
            z: source.z,
            height: BARRIER_HEIGHT,
            openings: source.openings
        }))
    );
    const railings: TierRailing[] = railingSources.flatMap((source) =>
        clipped(source.line).map((line) => ({ line, z: source.z, height: RAILING_HEIGHT }))
    );

    if (outline && ring) {
        for (const tier of tiers) {
            tier.pieces = intersectPolygons(tier.polygon, ring);
            tier.risers = clipPolylineInside(tier.front, ring);
            if (tier.sides) {
                tier.sides = tier.sides.flatMap((side) =>
                    clipPolylineInside(side.line, ring).map((line) => ({ line, baseZ: side.baseZ }))
                );
            }
        }
        const inside = (p: PlanPoint) => pointInOutline(outline, p, 1);
        const clippedLights = edgeLights.flatMap((light) =>
            clipPolylineInside([light.a, light.b], ring).flatMap((run) =>
                run.slice(1).map((b, i): TierLine => ({ a: run[i], b, z: light.z }))
            )
        );
        return {
            tiers,
            steps: steps.filter((step) =>
                inside({
                    x: step.polygon.reduce((sum, p) => sum + p.x, 0) / step.polygon.length,
                    y: step.polygon.reduce((sum, p) => sum + p.y, 0) / step.polygon.length
                })
            ),
            handrails,
            edgeLights: clippedLights,
            aisleLights: aisleLights.filter((light) =>
                inside({ x: (light.a.x + light.b.x) / 2, y: (light.a.y + light.b.y) / 2 })
            ),
            barriers,
            railings,
            stairWalls
        };
    }
    return { tiers, steps, handrails, edgeLights, aisleLights, barriers, railings, stairWalls };
}

// --- Levels -----------------------------------------------------------------------------------------

const levelOutlines = new WeakMap<object, RoomOutline>();

/** A level footprint as an outline the floor derivation can clip to (it has no screen edge, and needs none). */
function footprintOutline(level: RoomLevel): RoomOutline {
    let outline = levelOutlines.get(level.outline);
    if (!outline) {
        outline = { screenEdge: '', vertices: level.outline.vertices };
        levelOutlines.set(level.outline, outline);
    }
    return outline;
}

/** The model with everything raised by `dz` mm and stamped with the level it belongs to. */
function raised(model: TierModel, dz: number, level: string): TierModel {
    const up = (z: number) => z + dz;
    return {
        tiers: model.tiers.map((t) => ({
            ...t,
            z: up(t.z),
            baseZ: up(t.baseZ),
            level,
            ...(t.sides ? { sides: t.sides.map((side) => ({ ...side, baseZ: up(side.baseZ) })) } : {})
        })),
        steps: model.steps.map((s) => ({ ...s, z: up(s.z), baseZ: up(s.baseZ), level })),
        handrails: model.handrails.map((line) => line.map((p) => ({ ...p, z: up(p.z) }))),
        edgeLights: model.edgeLights.map((l) => ({ ...l, z: up(l.z) })),
        aisleLights: model.aisleLights.map((l) => ({ ...l, z: up(l.z) })),
        barriers: model.barriers.map((b) => ({ ...b, z: up(b.z) })),
        railings: model.railings.map((r) => ({ ...r, z: up(r.z) })),
        stairWalls: model.stairWalls.map((w) => ({ ...w, line: w.line.map((p) => ({ ...p, z: up(p.z) })) }))
    };
}

/**
 * A room with balconies: the main floor's seats make the main tiers, and every level's seats make their own tiers
 * on top of its slab (seat heights are absolute, so each level measures them from its elevation, and a balcony at
 * 4.5 m is literal instead of being squeezed as implausible). The models are joined; every tier and step says which
 * level it is on, and `levels` carries the slabs for the height queries.
 */
function deriveLevels(
    seats: readonly TierSeat[],
    room: TierRoom,
    geometry: RoomGeometry,
    platforms: readonly TierPlatformSpec[]
): TierModel {
    const byLevel = new Map<string, TierSeat[]>();
    for (const seat of seats) {
        const id = levelOf(geometry, seat);
        const list = byLevel.get(id) ?? [];
        list.push(seat);
        byLevel.set(id, list);
    }
    const main = deriveFloor(
        byLevel.get('main') ?? [],
        room,
        platforms.filter((p) => !p.level)
    );
    const parts: TierModel[] = [main];
    const slabs: TierLevel[] = [];
    for (const level of geometry.levels) {
        slabs.push({ id: level.id, elevation: level.elevation, ring: tessellate(level.outline).points });
        const own = (byLevel.get(level.id) ?? []).map((s) => ({ ...s, z: Math.max(0, s.z - level.elevation) }));
        const model = deriveFloor(
            own,
            {
                width: room.width,
                depth: room.depth,
                geometry: { version: 1, outline: footprintOutline(level), levels: [] }
            },
            platforms.filter((p) => p.level === level.id)
        );
        parts.push(raised(model, level.elevation, level.id));
    }
    return {
        tiers: parts.flatMap((p) => p.tiers),
        steps: parts.flatMap((p) => p.steps),
        handrails: parts.flatMap((p) => p.handrails),
        edgeLights: parts.flatMap((p) => p.edgeLights),
        aisleLights: parts.flatMap((p) => p.aisleLights),
        barriers: parts.flatMap((p) => p.barriers),
        railings: parts.flatMap((p) => p.railings),
        stairWalls: parts.flatMap((p) => p.stairWalls),
        levels: slabs
    };
}

/**
 * The rise (mm above the main floor) each seat stands at: `resolveRise` per floor, so a balcony's absolute height
 * is kept and only garbage heights within one floor are squeezed. Without levels it is `resolveRise` of all seats.
 */
export function resolveSeatRise(
    seats: readonly { x: number; y: number; z: number }[],
    geometry: RoomGeometry | null | undefined
): (seat: { x: number; y: number; z: number }) => number {
    if (!geometry?.levels?.length) {
        const rise = resolveRise(seats);
        return (seat) => rise(seat.z);
    }
    const byLevel = new Map<string, { z: number }[]>();
    for (const seat of seats) {
        const id = levelOf(geometry, seat);
        const list = byLevel.get(id) ?? [];
        list.push(seat);
        byLevel.set(id, list);
    }
    const rises = new Map<string, (z: number) => number>();
    for (const [id, list] of byLevel) {
        const elevation = geometry.levels.find((l) => l.id === id)?.elevation ?? 0;
        const rise = resolveRise(list.map((s) => ({ z: Math.max(0, s.z - elevation) })));
        rises.set(id, (z) => elevation + rise(Math.max(0, z - elevation)));
    }
    return (seat) => (rises.get(levelOf(geometry, seat)) ?? resolveRise([seat]))(seat.z);
}

// --- Queries --------------------------------------------------------------------------------------

export function insidePolygon(polygon: readonly PlanPoint[], p: PlanPoint): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
    }
    return inside;
}

/**
 * Height of the walkable surface (a tier or a stair tread) at a plan position on one level; 0 on the floor. `level`
 * is 'main' or a level id: a point under a balcony asks for 'main' and gets the stalls floor, not the slab.
 */
export function tierHeightAt(model: TierModel, p: PlanPoint, level = 'main'): number {
    let height = 0;
    const slab = model.levels?.find((l) => l.id === level);
    if (slab && insidePolygon(slab.ring, p)) height = slab.elevation;
    for (const tier of model.tiers) {
        if ((tier.level ?? 'main') !== level) continue;
        if (tier.z > height && insidePolygon(tier.polygon, p)) height = tier.z;
    }
    for (const step of model.steps) {
        if ((step.level ?? 'main') !== level) continue;
        if (step.isSolid && step.z > height && insidePolygon(step.polygon, p)) height = step.z;
    }
    return height;
}

/** The highest step a walker climbs without a ramp, mm (the walk collision's `STEP_MAX_M`). */
export const WALK_STEP_MM = 450;

/**
 * The floor a walker at height `from` (mm) stands on at a plan position, in a room with levels: the highest
 * surface of any level that is at most one step above where the walker is, else the base floor. A walker on the
 * stalls floor under a balcony keeps walking on it, one who climbed the stairs walks on the slab.
 */
export function tierHeightNear(model: TierModel, p: PlanPoint, from: number, stepMax = WALK_STEP_MM): number {
    let best = 0;
    for (const id of ['main', ...(model.levels ?? []).map((l) => l.id)]) {
        const h = tierHeightAt(model, p, id);
        if (h <= from + stepMax && h > best) best = h;
    }
    return best;
}
